import type ImageType from "@travily/types/shared/Image";
import { Avatar } from "@ui/components/Avatar";
import { Icon, type IconProps } from "@ui/components/Icon";
import { StandardButton } from "@ui/components/StandardButton";
import { formatRelativeTime } from "@ui/lib/format-relative-time";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

export interface NotificationAction {
  label: string;
  href: string;
  method: "post" | "patch" | "delete";
  variant: "primary" | "secondary";
}

interface NotificationActor {
  name: string;
  avatar: ImageType | null;
  initial: string;
  handle: string;
  profile_type: string;
}

const ICON_BADGE: Record<string, { name: IconProps["name"]; type: IconProps["type"] }> = {
  heart: { name: "heart", type: "sticker" },
  confetti: { name: "confetti", type: "sticker" },
  shop: { name: "shop", type: "sticker" },
};

export interface NotificationItemProps {
  actor: NotificationActor;
  actions: NotificationAction[];
  created_at: string;
  icon: string;
  message: string;
  onAction: (action: NotificationAction) => void;
  seen: boolean;
  url?: string | null;
}

function renderWithBoldActor(text: string, actorName: string): ReactNode {
  const index = text.indexOf(actorName);
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <strong className="font-bold">{actorName}</strong>
      {text.slice(index + actorName.length)}
    </>
  );
}

export function NotificationItem({
  actor,
  actions,
  created_at,
  icon,
  message,
  onAction,
  seen,
  url,
}: NotificationItemProps) {
  const badge = ICON_BADGE[icon];

  const content = (
    <div className="flex items-start gap-3">
      <AccountAvatar account={actor} className="shrink-0" shape="circle" size="40">
        {badge && (
          <Avatar.Badge>
            <Icon name={badge.name} size="20" type={badge.type} />
          </Avatar.Badge>
        )}
      </AccountAvatar>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-base text-neutral-900">{renderWithBoldActor(message, actor.name)}</p>
        <p className="text-sm font-semibold text-neutral-400">{formatRelativeTime(created_at)}</p>
      </div>
      {!seen && <div className="mt-2 size-3 shrink-0 rounded-full bg-red-400" />}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      {url ? (
        <Link className="no-underline hover:opacity-80" href={url}>
          {content}
        </Link>
      ) : (
        content
      )}
      {actions.length > 0 && (
        <div className="flex gap-3 pl-14">
          {actions.map((action) => (
            <StandardButton
              className="h-8 flex-1 px-4 py-0 font-normal"
              color={action.variant === "primary" ? "pink" : "neutral"}
              hierarchy="primary"
              key={action.label}
              onPress={() => onAction(action)}
              size="44"
            >
              {action.label}
            </StandardButton>
          ))}
        </div>
      )}
    </div>
  );
}
